// 📦 SEARCH-WORK 2026-07-02 — DEFER. Correct + 19 tests, but NO consumer, and pg_trgm
//    already handles Georgian inflection (87%). Do NOT wire to prod. See core/SEARCH_WORK_STATUS.md.
/**
 * core/georgian-stem.mjs — reduce an inflected Georgian noun/name to its stem
 * (ფუძე) for MATCHING. Applies to EVERYTHING searchable: districts, streets,
 * place names, surnames — and above all the QUERY side (a client types "ვაკეზე",
 * "ცაგარელზე", "თბილისში"; the gazetteer stores the nominative).
 *
 * Algorithm (per Georgian noun morphology):
 *   1. strip one POSTPOSITION   (-ში -ზე -თან -დან -მდე -თვის -კენ -გან -ვით)
 *   2. strip CASE endings to the bare stem, iteratively
 *        (-ის -ით -ად -მა -ს -თ -მ -ი -ა -ე), protecting უკვეცელი vowel-stems ო/უ
 *   3. strip the PLURAL infix -ებ
 *   4. apply a small SYNCOPE dictionary (კუმშვადი: წყალი/წყლის -> წყალ)
 *
 * Recall-biased and CONSISTENT: the guarantee is that every inflection of one word
 * collapses to the SAME stem (so query matches gazetteer), not that the stem is the
 * linguistically "canonical" root. Over-stem is safe — a shared stem is a match
 * CANDIDATE disambiguated by geo, never an asserted identity.
 */
const KA = /[ა-ჿ]/
const MIN = 3 // never strip below 3 Georgian chars (protects short names: გია, ანა)

const POSTPOSITIONS = ['ისთვის', 'ებისთვის', 'თვის', 'ამდე', 'მდე', 'დან', 'კენ', 'თან', 'ვით', 'გან', 'ზე', 'ში']
  .sort((a, b) => b.length - a.length)
// case/vowel endings stripped iteratively; ო/უ/ვ are NOT here (protected vowel-stems)
const CASES = ['ის', 'ით', 'ად', 'მა', 'ს', 'თ', 'მ', 'ი', 'ა', 'ე']
  .sort((a, b) => b.length - a.length)

// კუმშვადი / irregular stems: bare-stripped form -> canonical stem. Extensible;
// most address/surname stems (-ძე -შვილი -ია) do NOT syncopate, so this stays small.
const SYNCOPE = {
  'წყლ': 'წყალ', 'მგლ': 'მგელ', 'ფოთლ': 'ფოთოლ', 'ქვეყნ': 'ქვეყან',
  // 2026-07-18 (Desk search hunt): the two Tbilisi districts whose names syncopate —
  // their NORMAL genitive ("ისნის რაიონი", "ავლაბრის ბინა") lost the district filter.
  'ისნ': 'ისან',       // ისანი → ისნის/ისნიდან
  'ავლაბრ': 'ავლაბარ', // ავლაბარი → ავლაბრის/ავლაბრიდან
}

function stripOne(w, list) {
  for (const suf of list) if (w.endsWith(suf) && w.length - suf.length >= MIN) return w.slice(0, -suf.length)
  return w
}

export function georgianStem(word) {
  if (!word || !KA.test(word)) return word
  let w = stripOne(word, POSTPOSITIONS)          // 1. one postposition
  for (;;) { const n = stripOne(w, CASES); if (n === w) break; w = n } // 2. cases -> bare stem
  if (w.endsWith('ებ') && w.length - 2 >= MIN) w = w.slice(0, -2)      // 3. plural
  return SYNCOPE[w] || w                          // 4. syncope dictionary
}

// Stem every whitespace-separated token (names/streets are multi-word).
export function georgianStemPhrase(s) {
  return String(s).split(/\s+/).map(georgianStem).join(' ')
}
